import { AvatarCluster } from "@app/components/assistant/conversation/ChatAvatars";
import {
  ChatRowSuffix,
  chatRowClassName,
  UnreadBadge,
} from "@app/components/assistant/conversation/ChatsList";
import { MemoairSupportChats } from "@app/components/assistant/conversation/sidebar/MemoairSupportChats";
import { PodMenu, usePodMenu } from "@app/components/pod/PodMenu";
import { ExternalPill } from "@app/components/shared/ExternalPill";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import { usePodConversations } from "@app/hooks/conversations/usePodConversations";
import { useActiveConversationId } from "@app/hooks/useActiveConversationId";
import { useSidebarProjectExpanded } from "@app/hooks/useSidebarProjectExpanded";
import { useAppRouter } from "@app/lib/platform";
import { getSpaceIcon } from "@app/lib/spaces";
import { removeDiacritics, subFilter } from "@app/lib/utils";
import { getPodConversationRoute, getPodRoute } from "@app/lib/utils/router";
import type {
  GetBySpacesSummaryResponseBody,
  PodConversationListItemType,
} from "@app/types/api/assistant/conversation/spaces";
import type { ChatSummaryType } from "@app/types/api/chats";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import { stripMarkdown } from "@app/types/shared/utils/markdown";
import type { PodType } from "@app/types/space";
import type { WorkspaceType } from "@app/types/user";
import {
  ChevronRight,
  cn,
  Icon,
  LoadingBlock,
  NavigationListItem,
  NavigationListItemAction,
} from "@dust-tt/sparkle";
import { memo, useCallback, useContext, useRef, useState } from "react";

const conversationsPreviewCount = 5;
const conversationsPageStep = 10;

export const projectRowClassName = cn(
  chatRowClassName,
  "[&_.peer\/menu-button]:py-2 [&_.peer\/menu-button]:font-medium"
);
function matchesTitleFilter(titleFilter: string, value: string): boolean {
  return subFilter(
    removeDiacritics(titleFilter).toLowerCase(),
    removeDiacritics(value).toLowerCase()
  );
}

function conversationAvatars(conversation: PodConversationListItemType) {
  const people =
    conversation.avatars.length > 0
      ? conversation.avatars
      : conversation.creator
        ? [conversation.creator]
        : [];
  return people.map((person, index) => ({
    key: `${person.name}-${index}`,
    name: person.name,
    visual: person.visual,
  }));
}

function PodConversationRow({
  conversation,
  owner,
  podId,
  isSelected,
}: {
  conversation: PodConversationListItemType;
  owner: WorkspaceType;
  podId: string;
  isSelected: boolean;
}) {
  const { sidebarOpen, setSidebarOpen } = useContext(SidebarContext);
  const name = conversation.title || "New conversation";
  const description = stripMarkdown(conversation.description ?? "");

  return (
    <NavigationListItem
      selected={isSelected}
      label={name}
      labelAnimation={conversation.isRunningAgentLoop ? "streaming" : "none"}
      description={description || undefined}
      href={getPodConversationRoute(owner.sId, podId, conversation.id)}
      shallow
      hasActivity={conversation.unreadMessageCount > 0}
      suffix={
        <ChatRowSuffix
          activityAt={conversation.updated}
          unreadCount={conversation.unreadMessageCount}
          name={name}
        />
      }
      className={chatRowClassName}
      avatar={<AvatarCluster avatars={conversationAvatars(conversation)} />}
      onClick={async () => {
        if (sidebarOpen) {
          setSidebarOpen(false);
          await new Promise((resolve) => setTimeout(resolve, 600));
        }
      }}
    />
  );
}

function PodConversationRows({
  conversations,
  isLoading,
  hasMore,
  loadMore,
  owner,
  podId,
  forceShowAll,
  hideEmptyState,
}: {
  conversations: PodConversationListItemType[];
  isLoading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  owner: WorkspaceType;
  podId: string;
  forceShowAll: boolean;
  hideEmptyState: boolean;
}) {
  const activeConversationId = useActiveConversationId();
  const [visibleCount, setVisibleCount] = useState(conversationsPreviewCount);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-1.5 py-1 pl-2">
        {Array.from({ length: 2 }).map((_, index) => (
          <LoadingBlock key={index} className="h-10 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (conversations.length === 0) {
    return hideEmptyState ? null : (
      <div className="px-2.5 py-2 text-xs text-muted-foreground">
        No conversations yet
      </div>
    );
  }

  const visibleConversations = forceShowAll
    ? conversations
    : conversations.slice(0, visibleCount);
  const canShowMore =
    !forceShowAll && (conversations.length > visibleCount || hasMore);

  return (
    <>
      {visibleConversations.map((conversation) => (
        <PodConversationRow
          key={conversation.id}
          conversation={conversation}
          owner={owner}
          podId={podId}
          isSelected={conversation.id === activeConversationId}
        />
      ))}
      {canShowMore && (
        <button
          type="button"
          className="flex h-8 w-full items-center rounded-lg px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-hover hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-highlight-300 motion-reduce:transition-none"
          onClick={() => {
            const nextCount = visibleCount + conversationsPageStep;
            setVisibleCount(nextCount);
            if (nextCount > conversations.length) {
              loadMore();
            }
          }}
        >
          Show more
        </button>
      )}
    </>
  );
}

interface PodListItemProps {
  pod: PodType;
  isStarred: boolean;
  unreadCount: number;
  hasUnread: boolean;
  owner: WorkspaceType;
  titleFilter: string;
  memoairChats: ChatSummaryType[];
  activeChatId: string | null;
  moveConversationToPod: (
    conversation: ConversationWithoutContentType,
    pod: PodType
  ) => Promise<boolean>;
}

const PodListItem = memo(
  ({
    pod,
    isStarred,
    unreadCount,
    hasUnread,
    owner,
    titleFilter,
    memoairChats,
    activeChatId,
    moveConversationToPod,
  }: PodListItemProps) => {
    const router = useAppRouter();
    const { sidebarOpen, setSidebarOpen } = useContext(SidebarContext);
    const dropZoneRef = useRef<HTMLDivElement>(null);
    const { isProjectExpanded, setProjectExpanded } = useSidebarProjectExpanded(
      pod.sId
    );
    const [hasOpened, setHasOpened] = useState(isProjectExpanded);

    const podPath = getPodRoute(owner.sId, pod.sId);

    const { isMenuOpen, menuTriggerPosition, handleMenuOpenChange } =
      usePodMenu();

    const activeConversationId = useActiveConversationId();
    const needle = titleFilter.trim();

    const {
      conversations,
      isConversationsLoading,
      hasMore,
      loadMore,
      mutateConversations: mutateAllConversations,
    } = usePodConversations({
      workspaceId: owner.sId,
      podId: pod.sId,
      filter: "all",
      options: { disabled: !hasOpened && needle === "" },
    });
    const { mutateConversations: mutateWithMeConversations } =
      usePodConversations({
        workspaceId: owner.sId,
        podId: pod.sId,
        filter: "with_me",
        options: { disabled: true },
      });
    const { mutateConversations: mutateGroupConversations } =
      usePodConversations({
        workspaceId: owner.sId,
        podId: pod.sId,
        filter: "group",
        options: { disabled: true },
      });

    const isSpaceSelected =
      router.asPath === podPath || router.asPath.startsWith(`${podPath}#`);

    const [isDragOver, setIsDragOver] = useState(false);
    const dragCounterRef = useRef(0);

    const handleDragStart = (e: React.DragEvent) => {
      e.preventDefault();
    };

    const handleDragEnter = (e: React.DragEvent) => {
      e.preventDefault();
      dragCounterRef.current++;
      if (dragCounterRef.current === 1) {
        setIsDragOver(true);
      }
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragOver(false);
      }
    };

    const handleDrop = useCallback(
      async (e: React.DragEvent) => {
        setIsDragOver(false);
        dragCounterRef.current = 0;
        const conversationId = e.dataTransfer.getData(
          "application/x-dust-conversation"
        );
        if (!conversationId) {
          return;
        }

        try {
          const conversationData = e.dataTransfer.getData("application/json");
          if (conversationData) {
            const conversationObj = JSON.parse(
              conversationData
            ) as ConversationWithoutContentType;
            const success = await moveConversationToPod(conversationObj, pod);
            if (success) {
              void mutateAllConversations();
              void mutateWithMeConversations();
              void mutateGroupConversations();
            }
          }
        } catch (error) {
          console.error("Error parsing conversation data:", error);
        }
      },
      [
        moveConversationToPod,
        pod,
        mutateAllConversations,
        mutateWithMeConversations,
        mutateGroupConversations,
      ]
    );

    const nameMatches = needle === "" || matchesTitleFilter(needle, pod.name);
    const matchingConversations = nameMatches
      ? conversations
      : conversations.filter((c) => matchesTitleFilter(needle, c.title));

    if (!nameMatches && matchingConversations.length === 0) {
      return null;
    }

    const isMemoairSupport = pod.systemKind === "memoair_support";
    const isOpen = isProjectExpanded || !nameMatches;
    if (isOpen && !hasOpened) {
      setHasOpened(true);
    }
    const toggleLabel = `${isOpen ? "Collapse" : "Expand"} ${pod.name}`;

    return (
      <div>
        <div className="group/project relative">
          <NavigationListItem
            ref={dropZoneRef}
            onDragStart={handleDragStart}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={cn(
              projectRowClassName,
              isDragOver && "ring-2 ring-inset rounded-xl"
            )}
            avatar={<span aria-hidden className="size-11 shrink-0" />}
            selected={isSpaceSelected && !isDragOver}
            label={pod.name}
            href={podPath}
            shallow
            hasActivity={hasUnread}
            suffix={
              isMemoairSupport || unreadCount > 0 ? (
                <span className="flex items-center gap-2">
                  {isMemoairSupport && <ExternalPill />}
                  <UnreadBadge unreadCount={unreadCount} name={pod.name} />
                </span>
              ) : undefined
            }
            onClick={async () => {
              setProjectExpanded(true);
              // Side bar is the floating sidebar that appears when the screen is small.
              if (sidebarOpen) {
                setSidebarOpen(false);
                // Wait a bit before moving to the new space to avoid the sidebar from flickering.
                await new Promise((resolve) => setTimeout(resolve, 600));
              }
            }}
            moreMenu={
              <PodMenu
                activePodId={pod.sId}
                pod={pod}
                owner={owner}
                isStarred={isStarred}
                trigger={<NavigationListItemAction />}
                isPodDisplayed={activeConversationId === pod.sId}
                isOpen={isMenuOpen}
                onOpenChange={handleMenuOpenChange}
                triggerPosition={menuTriggerPosition}
              />
            }
          />
          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={toggleLabel}
            title={toggleLabel}
            onClick={() => setProjectExpanded(!isProjectExpanded)}
            className="absolute left-2.5 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-xl border border-border bg-muted-background text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-highlight-300 motion-reduce:transition-none dark:border-border-night dark:bg-muted-background-night dark:text-muted-foreground-night dark:hover:text-primary-night"
          >
            <Icon
              visual={getSpaceIcon(pod)}
              size="md"
              className="group-hover/project:hidden group-focus-within/project:hidden"
            />
            <Icon
              visual={ChevronRight}
              size="md"
              className={cn(
                "hidden transition-transform duration-200 ease-out group-hover/project:block group-focus-within/project:block motion-reduce:transition-none",
                isOpen && "rotate-90"
              )}
            />
          </button>
        </div>
        <div
          className={cn(
            "grid transition-[grid-template-rows,opacity,visibility] duration-200 ease-out motion-reduce:transition-none",
            isOpen
              ? "visible grid-rows-[1fr] opacity-100"
              : "invisible grid-rows-[0fr] opacity-0"
          )}
          aria-hidden={!isOpen}
        >
          <div className="overflow-hidden">
            <div className="ml-8 flex flex-col gap-0.5 border-l border-border py-0.5 pl-1.5 dark:border-border-night">
              {isMemoairSupport && nameMatches && (
                <MemoairSupportChats
                  owner={owner}
                  chats={memoairChats}
                  activeChatId={activeChatId}
                />
              )}
              {hasOpened && (
                <PodConversationRows
                  conversations={matchingConversations}
                  isLoading={isConversationsLoading}
                  hasMore={hasMore}
                  loadMore={loadMore}
                  owner={owner}
                  podId={pod.sId}
                  forceShowAll={!nameMatches}
                  hideEmptyState={isMemoairSupport}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export function renderPodsList({
  owner,
  summary,
  titleFilter,
  memoairChats,
  activeChatId,
  moveConversationToPod: moveConversationToPod,
}: {
  owner: WorkspaceType;
  summary: GetBySpacesSummaryResponseBody["summary"];
  titleFilter: string;
  memoairChats: ChatSummaryType[];
  activeChatId: string | null;
  moveConversationToPod: (
    conversation: ConversationWithoutContentType,
    pod: PodType
  ) => Promise<boolean>;
}) {
  if (!summary || summary.length === 0) {
    return null;
  }

  const memoairUnreadCount = memoairChats.reduce(
    (total, chat) => total + chat.unreadCount,
    0
  );

  return summary.map(
    ({ space, unreadConversations, nonParticipantUnreadConversations }) => {
      const chatsUnreadCount =
        space.systemKind === "memoair_support" ? memoairUnreadCount : 0;
      return (
        <PodListItem
          key={space.sId}
          pod={space}
          isStarred={space.isStarred}
          unreadCount={unreadConversations.length + chatsUnreadCount}
          hasUnread={
            unreadConversations.length > 0 ||
            nonParticipantUnreadConversations.length > 0 ||
            chatsUnreadCount > 0
          }
          owner={owner}
          titleFilter={titleFilter}
          memoairChats={memoairChats}
          activeChatId={activeChatId}
          moveConversationToPod={moveConversationToPod}
        />
      );
    }
  );
}
